/**
 * AdditionalInformationPanel — native rewrite of the legacy AdditionalInformation
 * sub-tab for the consumption cert (reason toggle + exterior-view + heater-nameplate
 * upload zones + message). Consumption never renders the construction-description zone
 * (`allowConstructionUpload` was always false).
 *
 * Uploads go through the shared PF v2 file-hub kinds via `useConsumptionUploadController`
 * (interaction-gated + value-guarded projection→DTO sync, §4b). Reason + message are
 * plain owned-field writes through the ops hook. Both uploads gate completeness (option
 * A, D-W2-1) via the DTO markers the controller keeps in sync.
 */

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'

import { FileType, certificateDocumentFileTypes } from 'constants/misc'
import { EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { Label } from 'components/common/Label'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { ToggleButton } from 'components/common/ToggleButton'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useConsumptionCertificateConfig } from '../useConsumptionCertificateConfig'
import { useConsumptionUploadController } from '../useConsumptionUploadSync'

const MAX_UPLOADS = 1

export interface AdditionalInformationPanelProps {
  cartItemId: CartItemId
}

export const AdditionalInformationPanel: React.FC<AdditionalInformationPanelProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'additional_information_page' })
  const { value, patch } = useConsumptionCertificateConfig(cartItemId)
  const { exterior, nameplate, uploadExterior, deleteExterior, uploadNameplate, deleteNameplate } =
    useConsumptionUploadController(cartItemId)

  return (
    <SectionedBorderBox title={t('title')}>
      <Stack gap="1.6rem" position="relative">

        <Stack gap=".6rem">

          <Label text={t('reason_for_issue_certificate')} />

          <Stack flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
            {Object.values(EnergyPassReason).map((reason) => (
              <ToggleButton
                key={reason}
                sx={{ height: '4rem', justifyContent: 'center' }}
                isSelected={value.reasonForEnergyPass === reason}
                label={t(`energy_certificate_reason.${reason}`)}
                onClick={() => patch({ reasonForEnergyPass: reason })}
              />
            ))}
          </Stack>

        </Stack>

        <FileAPIUpload
          title={<Trans t={t} i18nKey="exterior_view" values={{ uploadCount: exterior.entries.length, maximumUploads: MAX_UPLOADS }} />}
          maxNumberOfFiles={1}
          files={exterior.entries}
          acceptedFileTypes={[FileType.JPEG, FileType.PNG]}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => uploadExterior(acceptedFiles)}
          onDelete={(fileId) => deleteExterior(fileId)}
          onCancelUpload={(fileId) => deleteExterior(fileId)}
        />

        <FileAPIUpload
          title={<Trans t={t} i18nKey="heater_nameplate" values={{ uploadCount: nameplate.entries.length, maximumUploads: MAX_UPLOADS }} />}
          maxNumberOfFiles={1}
          files={nameplate.entries}
          acceptedFileTypes={certificateDocumentFileTypes}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => uploadNameplate(acceptedFiles)}
          onDelete={(fileId) => deleteNameplate(fileId)}
          onCancelUpload={(fileId) => deleteNameplate(fileId)}
        />

        <MUIInputField
          label={t('message_the_expert')}
          placeholder={t('message_the_expert_placeholder')}
          isMultiline
          value={value.messageToExpert ?? ''}
          onChange={(e) => patch({ messageToExpert: e.target.value })}
        />

      </Stack>
    </SectionedBorderBox>
  )
}
